import React, { useCallback, useMemo } from "react"
import types from "prop-types"
import clsx from "clsx"
import { isEqual } from "lodash-es"
import { useDrop } from "react-dnd"
import { useDispatch, useSelector } from "react-redux"
import {
  addMemberToDepartmentSegment,
  facilityDepartmentSegmentMemberIds,
  facilityId as getFacilityId,
  removeMemberFromDepartmentSegment,
  uniqueDepartmentMemberIds,
} from "reduxSlices/adminFacilitySlice"
import { useCustomName, useSCIMResourceManagement } from "hooks"
import * as API from "services/api"
import { DEPARTMENT_KEY } from "utils/customNameHelpers"
import { departmentSegmentShape } from "utils/propTypeShapes"
import { errorToast } from "shared/toast"
import MembersList from "./MembersList"
import { DraggableItemTypes } from "../DraggableItemTypes"

const DepartmentSegment = ({ className = "", departmentId, segment }) => {
  const { name: segmentName, members: segmentMembers } = segment

  const dispatch = useDispatch()

  const facilityId = useSelector(getFacilityId)
  const segmentMemberIds = useSelector(facilityDepartmentSegmentMemberIds({ departmentId, segmentName }), isEqual)
  const departmentMemberIds = useSelector(uniqueDepartmentMemberIds({ departmentId }), isEqual)

  const { departmentsManagedBySCIM } = useSCIMResourceManagement()

  const { nameInSentence: departmentNameInSentence } = useCustomName(DEPARTMENT_KEY)

  // Functions for adding a new member to the department
  const canAddNewDepartmentMemberToSegment = useCallback((draggableUserProfileIcon) => {
    if (departmentsManagedBySCIM && !departmentMemberIds.includes(draggableUserProfileIcon.id)) {
      return false
    }

    return !segmentMemberIds.includes(draggableUserProfileIcon.id)
  }, [departmentsManagedBySCIM, departmentMemberIds, segmentMemberIds])

  const addDepartmentSegmentMember = useCallback(async (draggableUserProfileIcon) => {
    const response = await API.createDepartmentMembership({
      departmentId,
      departmentMembership: {
        memberId: draggableUserProfileIcon.id,
        segmentName,
      },
      facilityId,
    })

    if (response.ok) {
      dispatch(addMemberToDepartmentSegment({ departmentId, member: response.data, segmentName }))
    } else {
      errorToast(`Something went wrong.  Unable to add ${departmentNameInSentence} member.`)
    }
  }, [departmentId, dispatch, facilityId, segmentName, departmentNameInSentence])

  // Functions for moving a member to a different segment
  const canMoveMemberToSegment = useCallback((draggableMemberProfileItem) => {
    if (draggableMemberProfileItem.departmentId !== departmentId) return false
    if (draggableMemberProfileItem.segmentName === segmentName) return false

    return true
  }, [departmentId, segmentName])

  const moveMemberToSegment = useCallback(async (draggableMemberProfileItem) => {
    const { memberId, segmentName: fromSegmentName, user: member } = draggableMemberProfileItem

    const alreadyInSegment = segmentMemberIds.includes(memberId)

    if (!alreadyInSegment) {
      dispatch(addMemberToDepartmentSegment({ departmentId, member, segmentName }))
    }
    dispatch(removeMemberFromDepartmentSegment({ departmentId, memberId, segmentName: fromSegmentName }))

    const response = await API.updateDepartmentMemberSegment({
      departmentId,
      facilityId,
      memberId,
      fromSegment: fromSegmentName,
      toSegment: segmentName,
    })

    if (!response.ok) {
      errorToast("Something went wrong.  Unable to change segment.")
      if (!alreadyInSegment) {
        dispatch(removeMemberFromDepartmentSegment({ departmentId, memberId, segmentName }))
      }
      dispatch(addMemberToDepartmentSegment({ departmentId, member, segmentName: fromSegmentName }))
    }
  }, [
    departmentId,
    dispatch,
    facilityId,
    segmentMemberIds,
    segmentName,
  ])

  // Drop zone setup
  // Handle two types of draggable items.
  // If the item is a user profile icon, attempts to add the user to the department.
  // If the item is a member profile icon, attempts to change the segment.
  const dropCallbacks = useMemo(
    () => (
      {
        [DraggableItemTypes.MEMBER_PROFILE_ICON]: {
          canDrop: canMoveMemberToSegment,
          drop: moveMemberToSegment,
        },
        [DraggableItemTypes.USER_PROFILE_ICON]: {
          canDrop: canAddNewDepartmentMemberToSegment,
          drop: addDepartmentSegmentMember,
        },
      }
    ),
    [
      canMoveMemberToSegment,
      moveMemberToSegment,
      canAddNewDepartmentMemberToSegment,
      addDepartmentSegmentMember,
    ],
  )

  const [collected, drop] = useDrop(() => ({
    accept: [
      DraggableItemTypes.MEMBER_PROFILE_ICON,
      DraggableItemTypes.USER_PROFILE_ICON,
    ],
    canDrop: (item, monitor) => (
      dropCallbacks[monitor.getItemType()].canDrop(item)
    ),
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
    drop: (item, monitor) => {
      dropCallbacks[monitor.getItemType()].drop(item)
    },
  }), [dropCallbacks])

  const classList = clsx(
    className,
    "flex gap-4 items-start",
    (
      collected.isOver && collected.canDrop && "bg-green-200 !border-green-800"
    ),
  )

  return (
    <div ref={drop} className={classList}>
      <span>{segmentName.toUpperCase()}</span>
      <MembersList className="grow" departmentId={departmentId} members={segmentMembers} segmentName={segmentName} />
    </div>
  )
}

DepartmentSegment.propTypes = {
  className: types.string,
  departmentId: types.number.isRequired,
  segment: departmentSegmentShape.isRequired,
}

export default DepartmentSegment
